import type { Core, Data } from '@strapi/types';
import { useMutation } from '@tanstack/react-query';

import { useAppContext } from '@/modules/app-context/use-app-context';
import { useApi } from '@/modules/utils/custom-hooks/use-api';

import { createProjectApi, editProjectApi, deleteProjectApi } from '../api/project.api';

export const useProjectApi = () => {
	const { onSuccess } = useApi({ queryKey: ['GET_PROJECT_LIST'] });
	const { setProject, setSearchProjectValue } = useAppContext();

	const createProject = useMutation({
		mutationKey: ['[POST]:CREATE_PROJECT'],
		mutationFn: (body: Core.Helpers.Controllers.GetRequestBody<'api::project.project', 'create'>) => createProjectApi(body),
		onSuccess: () => {
			onSuccess('T_SUCCESS_PROJECT_CREATED');
			setSearchProjectValue('');
		},
	});

	const editProject = useMutation({
		mutationKey: ['[PUT]:EDIT_PROJECT'],
		mutationFn: ({
			documentId,
			body,
		}: {
			documentId: Pick<Data.Entity<'api::project.project'>, 'documentId'>['documentId'];
			body: Core.Helpers.Controllers.GetRequestBody<'api::project.project', 'update'>;
		}) => editProjectApi({ body, documentId }),
		onSuccess: (data) => {
			setProject({
				customer: { documentId: data.data.customer?.documentId as string },
				documentId: data.data.documentId,
				id: data.data.id,
				country: data.data.country,
				title: data.data.title,
			});
			setSearchProjectValue('');
			onSuccess();
		},
	});

	const deleteProject = useMutation({
		mutationKey: ['[DELETE]:DELETE_PROJECT'],
		mutationFn: ({ documentId }: { documentId: Pick<Data.Entity<'api::project.project'>, 'documentId'>['documentId'] }) =>
			deleteProjectApi({ documentId }),
		onSuccess: () => {
			setProject(null);
			onSuccess('T_SUCCESS_PROJECT_DELETED');
			setSearchProjectValue('');
		},
	});

	const isPending = createProject.isPending || editProject.isPending || deleteProject.isPending;
	const error = createProject.error || editProject.error || deleteProject.error;

	return {
		createProject,
		editProject,
		deleteProject,
		error,
		isPending,
	};
};
